/**
 * Performance Limits — ECharts option builders
 *
 * Exports functions for: Ragone Low/High Energy, Energy Density vs Year,
 * Power Density vs Year, Ragone by Year, Performance Envelopes.
 */

import {
  J_TO_WH,
  TEXT_PRIMARY, TEXT_SECONDARY, TEXT_TERTIARY,
  FONT_BODY, FONT_TITLE, PALETTE,
  _rgba, _lerpColor, _yearColor, groupColor, _scatterStyle,
  _baseTitle, _compactTitle, _baseGrid, _compactGrid, _baseTooltip, _baseAxis, _logYAxis, _baseLegend, _compactLegend,
  _hullSeries, _hullSwappedSeries, _xy, _xySwapped,
} from './radarChartTheme.js';

// ---------------------------------------------------------------------------
// Constants — Corporate Design
// ---------------------------------------------------------------------------

const COLOR_LOW_ENERGY = '#12a0be';
const COLOR_HIGH_ENERGY = '#ea5b0c';

const PERF_GROUP_COLORS = {
  'Low-Energy Chemistries': COLOR_LOW_ENERGY,
  'High-Energy Chemistries': COLOR_HIGH_ENERGY,
};

// ---------------------------------------------------------------------------
// Shared percentile-band builder
// ---------------------------------------------------------------------------

function _buildPercentileBandChart(bands, yLabel, title, convertEnergy = false, scatterPoints = null, compact = false) {
  if (!bands) return {};
  const groups = Object.keys(bands).filter((g) => g !== 'analysis ongoing');
  const yearSet = new Set();
  groups.forEach((g) => bands[g].forEach((d) => yearSet.add(String(d.year))));
  const years = [...yearSet].sort();

  const series = [];
  const legendData = [];

  groups.forEach((group) => {
    const color = PERF_GROUP_COLORS[group] || groupColor(group);
    legendData.push(group);

    const byYear = {};
    bands[group].forEach((d) => { byYear[String(d.year)] = d; });
    const cf = convertEnergy ? J_TO_WH : 1;

    // All sub-series share the same name so ECharts legend hover highlights them together
    series.push({
      name: group,
      type: 'line',
      stack: `band_${group}`,
      _role: 'p25',
      data: years.map((y) => (byYear[y] ? byYear[y].p25 * cf : null)),
      symbol: 'none',
      lineStyle: { width: 0 },
      itemStyle: { color },
      emphasis: { focus: 'series' },
      blur: { lineStyle: { opacity: 0 }, areaStyle: { opacity: 0 } },
      silent: true,
      showInLegend: false,
    });

    series.push({
      name: group,
      type: 'line',
      stack: `band_${group}`,
      _role: 'band',
      data: years.map((y) => {
        if (!byYear[y]) return null;
        return Math.max(0, (byYear[y].p75 - byYear[y].p25) * cf);
      }),
      symbol: 'none',
      lineStyle: { width: 0 },
      areaStyle: { color: _rgba(color, 0.12) },
      itemStyle: { color },
      emphasis: { focus: 'series', areaStyle: { color: _rgba(color, 0.25) } },
      blur: { lineStyle: { opacity: 0 }, areaStyle: { opacity: 0.03 } },
      silent: true,
      showInLegend: false,
    });

    series.push({
      name: group,
      type: 'line',
      _role: 'median',
      data: years.map((y) => (byYear[y] ? byYear[y].median * cf : null)),
      symbol: 'diamond',
      symbolSize: 7,
      lineStyle: { color, width: 2 },
      itemStyle: { color },
      emphasis: {
        focus: 'series',
        lineStyle: { width: 3 },
        itemStyle: { borderColor: '#fff', borderWidth: 2, shadowBlur: 6, shadowColor: 'rgba(0,0,0,0.4)' },
      },
      blur: {
        lineStyle: { opacity: 0.15, width: 1 },
        itemStyle: { opacity: 0.15 },
      },
    });

    // Individual scatter points overlaid on the band
    if (scatterPoints && scatterPoints[group]) {
      const isLow = group.toLowerCase().includes('low');
      series.push({
        name: group,
        type: 'scatter',
        _role: 'pts',
        data: scatterPoints[group].map((pt) => [pt.year, pt.value * cf]),
        symbolSize: 4,
        symbol: isLow ? 'triangle' : 'circle',
        itemStyle: { color, opacity: 0.5 },
        emphasis: { focus: 'series', itemStyle: { opacity: 0.8 } },
        blur: { itemStyle: { opacity: 0.05 } },
        z: 3,
        showInLegend: false,
        tooltip: { show: false },
      });
    }
  });

  return {
    title: compact ? _compactTitle(title) : _baseTitle(title),
    tooltip: _baseTooltip({
      trigger: 'axis',
      formatter(params) {
        if (!params || params.length === 0) return '';
        const year = String(params[0].axisValue);
        const unit = yLabel.match(/\((.+)\)/)?.[1] || '';
        const cf = convertEnergy ? J_TO_WH : 1;
        let html = `<b>${year}</b>`;
        groups.forEach((group) => {
          const bandData = bands[group];
          if (!bandData) return;
          const d = bandData.find((b) => String(b.year) === year);
          if (!d) return;
          const color = PERF_GROUP_COLORS[group] || groupColor(group);
          const median = (d.median * cf).toFixed(1);
          const p25 = (d.p25 * cf).toFixed(1);
          const p75 = (d.p75 * cf).toFixed(1);
          html += `<br/><span style="color:${color};">\u25CF</span> ${group}: ${median} ${unit} (${p25}–${p75})`;
        });
        return html;
      },
    }),
    legend: compact ? _compactLegend(legendData) : _baseLegend(legendData),
    grid: compact ? _compactGrid() : _baseGrid(),
    xAxis: _baseAxis('Year', {
      type: 'category',
      data: years,
      axisLabel: { fontSize: compact ? 10 : 11 },
    }),
    yAxis: _baseAxis(yLabel, { nameGap: compact ? 45 : 55, nameRotate: 90, fontSize: compact ? 10 : 11 }),
    series,
  };
}

// ---------------------------------------------------------------------------
// 1. Ragone Low-Energy vs High-Energy
// ---------------------------------------------------------------------------

export function buildRagoneLowHighEnergy(data, basis = 'grav') {
  const xLabel = basis === 'vol' ? 'Peak Power Density (W/L)' : 'Peak Power Density (W/kg)';
  const yLabel = basis === 'vol' ? 'Energy Density (Wh/L)' : 'Energy Density (Wh/kg)';

  const lowPts = (data.ragone_low_energy || []).filter(
    (p) => p != null && _xySwapped(p, basis)[0] != null && _xySwapped(p, basis)[1] != null
  );
  const highPts = (data.ragone_high_energy || []).filter(
    (p) => p != null && _xySwapped(p, basis)[0] != null && _xySwapped(p, basis)[1] != null
  );

  const series = [
    {
      name: 'High-Energy Chemistries',
      type: 'scatter',
      data: highPts.map((p) => ({ value: _xySwapped(p, basis), _meta: p })),
      symbolSize: 7,
      symbol: 'circle',
      itemStyle: _scatterStyle(COLOR_HIGH_ENERGY),
      emphasis: {
        focus: 'series',
        itemStyle: { opacity: 1, borderColor: '#fff', borderWidth: 2, shadowBlur: 6, shadowColor: 'rgba(0,0,0,0.4)' },
      },
      blur: {
        itemStyle: { opacity: 0.08 },
      },
      z: 3,
    },
    {
      name: 'Low-Energy Chemistries',
      type: 'scatter',
      data: lowPts.map((p) => ({ value: _xySwapped(p, basis), _meta: p })),
      symbolSize: 7,
      symbol: 'triangle',
      itemStyle: _scatterStyle(COLOR_LOW_ENERGY),
      emphasis: {
        focus: 'series',
        itemStyle: { opacity: 1, borderColor: '#fff', borderWidth: 2, shadowBlur: 6, shadowColor: 'rgba(0,0,0,0.4)' },
      },
      blur: {
        itemStyle: { opacity: 0.08 },
      },
      z: 3,
    },
  ];

  const basisKey = basis === 'vol' ? 'vol' : 'grav';

  const addHulls = (hullMap, baseColor) => {
    if (!hullMap) return;
    Object.entries(hullMap).forEach(([group, hullData]) => {
      const pts = hullData?.[basisKey];
      const hull = _hullSwappedSeries(group, pts, baseColor);
      if (hull) {
        hull.showInLegend = false;
        series.push(hull);
      }
    });
  };

  addHulls(data.hulls_low_energy, COLOR_LOW_ENERGY);
  addHulls(data.hulls_high_energy, COLOR_HIGH_ENERGY);

  return {
    title: _baseTitle('Ragone Plot — Low vs High Energy'),
    tooltip: _baseTooltip({
      formatter(params) {
        const m = params.data?._meta;
        if (!m) return '';
        return [
          `<b>${m.name}</b>`,
          `Manufacturer: ${m.manufacturer}`,
          `Group: ${m.group}`,
          `Power: ${params.value[0]?.toFixed(1)} ${basis === 'vol' ? 'W/L' : 'W/kg'}`,
          `Energy: ${params.value[1]?.toFixed(1)} ${basis === 'vol' ? 'Wh/L' : 'Wh/kg'}`,
        ].join('<br/>');
      },
    }),
    legend: _baseLegend(['Low-Energy Chemistries', 'High-Energy Chemistries']),
    grid: _baseGrid(),
    xAxis: _baseAxis(xLabel),
    yAxis: _baseAxis(yLabel, { nameGap: 55, nameRotate: 90 }),
    series,
  };
}

// ---------------------------------------------------------------------------
// 2. Energy Density vs Year
// ---------------------------------------------------------------------------

export function buildEnergyDensityVsYear(data, basis = 'grav') {
  const eField = basis === 'vol' ? 'e_vol' : 'e_grav';
  const bands = (basis === 'vol' ? data.energy_density_bands_vol : null) || data.energy_density_bands;
  const unit = basis === 'vol' ? 'Wh/L' : 'Wh/kg';
  const scatterPts = { 'Low-Energy Chemistries': [], 'High-Energy Chemistries': [] };
  (data.ragone_low_energy || []).forEach((pt) => {
    if (pt[eField] != null && pt.year) scatterPts['Low-Energy Chemistries'].push({ year: pt.year, value: pt[eField] });
  });
  (data.ragone_high_energy || []).forEach((pt) => {
    if (pt[eField] != null && pt.year) scatterPts['High-Energy Chemistries'].push({ year: pt.year, value: pt[eField] });
  });
  return _buildPercentileBandChart(
    bands,
    `Energy Density (${unit})`,
    'Energy Density vs Year',
    true,
    scatterPts
  );
}

// ---------------------------------------------------------------------------
// 3. Power Density vs Year
// ---------------------------------------------------------------------------

export function buildPowerDensityVsYear(data, basis = 'grav') {
  const pField = basis === 'vol' ? 'p_vol' : 'p_grav';
  const bands = (basis === 'vol' ? data.power_density_bands_vol : null) || data.power_density_bands;
  const unit = basis === 'vol' ? 'W/L' : 'W/kg';
  const scatterPts = { 'Low-Energy Chemistries': [], 'High-Energy Chemistries': [] };
  (data.ragone_low_energy || []).forEach((pt) => {
    if (pt[pField] != null && pt.year) scatterPts['Low-Energy Chemistries'].push({ year: pt.year, value: pt[pField] });
  });
  (data.ragone_high_energy || []).forEach((pt) => {
    if (pt[pField] != null && pt.year) scatterPts['High-Energy Chemistries'].push({ year: pt.year, value: pt[pField] });
  });
  return _buildPercentileBandChart(
    bands,
    `Peak Power Density (${unit})`,
    'Peak Power Density vs Year',
    false,
    scatterPts
  );
}

// ---------------------------------------------------------------------------
// 4. Ragone by Year
// ---------------------------------------------------------------------------

export function buildRagoneByYear(data, basis = 'grav') {
  const xLabel = basis === 'vol' ? 'Peak Power Density (W/L)' : 'Peak Power Density (W/kg)';
  const yLabel = basis === 'vol' ? 'Energy Density (Wh/L)' : 'Energy Density (Wh/kg)';
  const basisKey = basis === 'vol' ? 'vol' : 'grav';

  const byYear = {};
  (data.ragone_by_year || []).forEach((p) => {
    if (p == null || _xySwapped(p, basis)[0] == null || _xySwapped(p, basis)[1] == null) return;
    const yr = String(p.group);
    if (!byYear[yr]) byYear[yr] = [];
    byYear[yr].push(p);
  });

  const years = Object.keys(byYear).sort();
  const series = [];
  const legendData = [];

  years.forEach((yr) => {
    const color = _yearColor(yr);
    legendData.push(yr);

    series.push({
      name: yr,
      type: 'scatter',
      data: byYear[yr].map((p) => ({ value: _xySwapped(p, basis), _meta: p })),
      symbolSize: 7,
      itemStyle: _scatterStyle(color),
      emphasis: {
        focus: 'series',
        itemStyle: { opacity: 1, borderColor: '#fff', borderWidth: 2, shadowBlur: 6, shadowColor: 'rgba(0,0,0,0.4)' },
      },
      blur: {
        itemStyle: { opacity: 0.08 },
      },
      z: 3,
    });

    const hullPts = data.hulls_by_year?.[yr]?.[basisKey];
    const hull = _hullSwappedSeries(yr, hullPts, color);
    if (hull) {
      hull.showInLegend = false;
      series.push(hull);
    }
  });

  return {
    title: _baseTitle('Ragone Plot by Year'),
    tooltip: _baseTooltip({
      formatter(params) {
        const m = params.data?._meta;
        if (!m) return '';
        return [
          `<b>${m.name}</b>`,
          `Manufacturer: ${m.manufacturer}`,
          `Year: ${m.year}`,
          `Power: ${params.value[0]?.toFixed(1)} ${basis === 'vol' ? 'W/L' : 'W/kg'}`,
          `Energy: ${params.value[1]?.toFixed(1)} ${basis === 'vol' ? 'Wh/L' : 'Wh/kg'}`,
        ].join('<br/>');
      },
    }),
    legend: _baseLegend(legendData),
    grid: _baseGrid(),
    xAxis: _baseAxis(xLabel),
    yAxis: _baseAxis(yLabel, { nameGap: 55, nameRotate: 90 }),
    series,
  };
}

// ---------------------------------------------------------------------------
// 5. Performance Envelopes (with year-coloured scatter + year hulls)
// ---------------------------------------------------------------------------

// Dual-hue palette: High-Energy = Batemo Orange, Low-Energy = Brand Teal
const ENVELOPE_BASE_COLORS = {
  'High-Energy': '#ea5b0c',
  'Low-Energy': '#12a0be',
};

// Opacity ramp: older = more transparent, newer = more opaque
const ENVELOPE_OPACITY = {
  2019: 0.2, 2020: 0.25, 2021: 0.35,
  2022: 0.5, 2023: 0.65, 2024: 0.8, 2025: 1.0,
};

const ENVELOPE_STROKE = {
  2019: 1, 2020: 1, 2021: 1.5,
  2022: 1.5, 2023: 2, 2024: 2, 2025: 2.5,
};

export function buildPerformanceEnvelope(
  data,
  basis = 'grav',
  yearToggles = { 2021: true, 2023: true, 2025: true }
) {
  const xLabel = basis === 'vol' ? 'Peak Power Density (W/L)' : 'Peak Power Density (W/kg)';
  const yLabel = basis === 'vol' ? 'Energy Density (Wh/L)' : 'Energy Density (Wh/kg)';
  const basisKey = basis === 'vol' ? 'vol' : 'grav';

  const activeYears = Object.entries(yearToggles)
    .filter(([, active]) => active)
    .map(([yr]) => String(yr));
  const maxActiveYear = activeYears.length > 0 ? String(Math.max(...activeYears.map(Number))) : '9999';

  const isValid = (p) => p != null && _xySwapped(p, basis)[0] != null && _xySwapped(p, basis)[1] != null;
  const inYearRange = (p) => p.year && p.year <= maxActiveYear;

  const lowPts = (data.ragone_low_energy || []).filter((p) => isValid(p) && inYearRange(p));
  const highPts = (data.ragone_high_energy || []).filter((p) => isValid(p) && inYearRange(p));

  const series = [];
  const legendData = [];

  const segYearHulls = data.hulls_by_segment_year || {};
  const activeYearNums = activeYears.map(Number).sort();

  ['High-Energy', 'Low-Energy'].forEach((segment) => {
    const segColor = ENVELOPE_BASE_COLORS[segment] || '#888';
    const pts = segment === 'High-Energy' ? highPts : lowPts;
    const segLabel = `${segment} Chemistries`;
    legendData.push(segLabel);

    // Scatter — same name as hulls so ECharts legend hover highlights all together
    series.push({
      name: segLabel,
      type: 'scatter',
      data: pts.map((p) => ({ value: _xySwapped(p, basis), _meta: p })),
      symbolSize: 7,
      symbol: segment === 'High-Energy' ? 'circle' : 'triangle',
      itemStyle: _scatterStyle(_rgba(segColor, 0.6)),
      emphasis: {
        focus: 'series',
        itemStyle: { opacity: 1, borderColor: '#fff', borderWidth: 2, shadowBlur: 6, shadowColor: 'rgba(0,0,0,0.4)' },
      },
      blur: {
        itemStyle: { opacity: 0.06 },
      },
      z: 3,
    });

    // Convex hulls — same name, hidden from legend (deduplicated by ECharts)
    activeYearNums.forEach((yr, idx) => {
      const key = `${segment}≤${yr}`;
      const hullPts = segYearHulls[key]?.[basisKey];
      if (!hullPts || hullPts.length < 3) return;

      const opacity = ENVELOPE_OPACITY[yr] || 0.5;
      const strokeW = ENVELOPE_STROKE[yr] || 1.5;

      const hull = _hullSwappedSeries(segLabel, hullPts, segColor, {
        lineStyle: { width: strokeW, opacity },
        areaStyle: { color: _rgba(segColor, opacity * 0.12) },
        z: 2 + idx,
      });
      if (hull) {
        hull.animationDelay = idx * 400;
        series.push(hull);
      }
    });
  });

  return {
    animation: true,
    animationDuration: 1000,
    animationEasing: 'cubicInOut',
    title: _baseTitle('Performance Envelopes — High vs Low Energy'),
    tooltip: _baseTooltip({
      formatter(params) {
        const m = params.data?._meta;
        if (!m) return params.seriesName || '';
        return [
          `<b>${m.name}</b>`,
          `Manufacturer: ${m.manufacturer}`,
          `Year: ${m.year}`,
          `Power: ${params.value[0]?.toFixed(1)} ${basis === 'vol' ? 'W/L' : 'W/kg'}`,
          `Energy: ${params.value[1]?.toFixed(1)} ${basis === 'vol' ? 'Wh/L' : 'Wh/kg'}`,
        ].join('<br/>');
      },
    }),
    legend: _baseLegend(legendData),
    grid: _baseGrid(),
    xAxis: _baseAxis(xLabel),
    yAxis: _baseAxis(yLabel, { nameGap: 55, nameRotate: 90 }),
    series,
  };
}
